import React, { createContext, useState, useEffect } from 'react';
import axios from '../api/client';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);

  const logout = async () => {
    try {
      await axios.post('/auth/logout');
    } catch (_) {
      // best-effort
    }
    setUser(null);
    setTenant(null);
    setPermissions({});
  };

  const fetchPermissions = async () => {
    try {
      const response = await axios.get('/role-permissions/me');
      setPermissions(response.data.permissions || {});
    } catch (error) {
      setPermissions({});
    }
  };

  // Admins always have full access (mirrors the backend's checkPermission bypass),
  // so the UI never hides something from an admin due to a stale/missing permissions fetch.
  const can = (module, action) => {
    if (user?.role === 'admin') return true;
    return !!permissions?.[module]?.[action];
  };

  const fetchUser = async () => {
    try {
      const response = await axios.get('/auth/me');
      setUser(response.data.user);
      setTenant(response.data.tenant);
      if (response.data.user) await fetchPermissions();
    } catch (error) {
      // Cookie invalid or expired — clear local state
      setUser(null);
      setTenant(null);
      setPermissions({});
    } finally {
      setLoading(false);
    }
  };

  // On mount, try to restore session from the httpOnly cookie
  useEffect(() => {
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Back/forward cache: after logout, pressing Back can bring a dashboard page back from
  // the browser's in-memory snapshot, session state and all, without a single request. On
  // a shared computer that is the next person reading the previous user's screen. When a
  // page is restored that way (event.persisted), drop what is on screen and ask the server
  // again — PrivateRoute shows a spinner while loading is true, then redirects if the
  // cookie is gone.
  useEffect(() => {
    const revalidate = (event) => {
      if (!event.persisted) return;
      setLoading(true);
      fetchUser();
    };
    window.addEventListener('pageshow', revalidate);
    return () => window.removeEventListener('pageshow', revalidate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post(
        '/auth/login',
        { email, password },
        { timeout: 10000 }
      );

      if (!response || !response.data) {
        return { success: false, message: 'No data received from server' };
      }

      // Cookie is set by the server — just load the user
      const userData = response.data;
      setUser(userData);
      await fetchUser();
      return { success: true, role: userData.role, mustChangePassword: userData.mustChangePassword };
    } catch (error) {
      if (error.code === 'ECONNABORTED' || error.message === 'Network Error' || !error.response) {
        return {
          success: false,
          message: 'Cannot connect to server. Please ensure the backend is running.',
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Login failed',
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post('/auth/register', userData);
      setUser(response.data);
      await fetchUser();
      return { success: true, role: response.data.role };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Registration failed',
      };
    }
  };

  return (
    <AuthContext.Provider value={{ user, tenant, permissions, can, loading, login, register, logout, fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
};
